import { useEffect, useRef } from "react";
import { CircularProgress } from "@mui/material";
import { toDate } from "helpers/dateHelper";
import { getFullName } from "helpers/utils";
import { useGetTicketMessages } from "hooks/useGetTicketMessages";

interface TicketMessagesProps {
  ticketID: number | string;
}

const isStaffMessage = (message: any) => {
  const senderType = (message?.senderType || "").toString().toLowerCase();
  if (senderType) {
    return (
      senderType === "member" ||
      senderType === "agent" ||
      senderType === "staff" ||
      senderType === "assistant"
    );
  }
  return Boolean(message?.memberID);
};

const getSenderName = (message: any) => {
  if (isStaffMessage(message)) {
    return message?.member?.name || "Сотрудник";
  }
  return (
    (message?.customer?.lastName || "") + " " + (message?.customer?.firstName || "") || "Клиент"
  );
};

export const TicketMessages = ({ ticketID }: TicketMessagesProps) => {
  const { data: allMessages = [], isLoading, isError } = useGetTicketMessages(ticketID);
  // Внутренние заметки (isInternal=true) живут в отдельной вкладке «Заметки» —
  // в основной переписке их не показываем, иначе дублируется.
  const messages = allMessages.filter((m: any) => !m?.isInternal);
  const listEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
  }, [messages?.length]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <CircularProgress size={20} />
      </div>
    );
  }

  if (isError) {
    return <div className="text-sm text-red-500">Не удалось загрузить ответы по тикету</div>;
  }

  if (!messages.length) {
    return <div className="text-sm text-gray-500">Пока нет ответов по этому тикету</div>;
  }

  return (
    <div
      className="ticket-messages flex flex-col gap-3 overflow-auto"
      style={{ minHeight: 420, maxHeight: "calc(100vh - 380px)", padding: 4 }}
    >
      {messages.map((message: any) => {
        const staff = isStaffMessage(message);
        return (
          <div key={message.id} className={`flex ${staff ? "justify-end" : "justify-start"}`}>
            <div
              style={{
                maxWidth: "85%",
                padding: "8px 12px",
                borderRadius: 12,
                background: staff ? "#1976d2" : "#f1f3f5",
                color: staff ? "#fff" : "#212529",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              <div className="text-xs mb-1" style={{ opacity: 0.85 }}>
                {getSenderName(message)}
              </div>
              {/* Image */}
              {message.message?.mimeType?.startsWith("image/") && (
                <div className="mb-2">
                  <img
                    src={message.message?.image}
                    alt="attachment"
                    style={{ maxWidth: "200px", borderRadius: 8 }}
                  />
                  <a href={message.message?.image} target="_blank">
                    Вложенный файл
                  </a>
                </div>
              )}
              {/* Video */}
              {String(message.message?.mimeType)?.startsWith("video/") && (
                <div className="mb-2">
                  <video
                    src={message.message?.image}
                    controls
                    style={{ maxWidth: "260px", borderRadius: 8 }}
                  />
                  <a href={message.message?.image} target="_blank">
                    Видео
                  </a>
                </div>
              )}
              {/* Audio */}
              {String(message.message?.mimeType)?.startsWith("audio/") && (
                <div className="mb-2">
                  <audio src={message.message?.image} controls style={{ maxWidth: "260px" }} />
                  <a href={message.message?.image} target="_blank">
                    Аудио
                  </a>
                </div>
              )}
              <div className="text-sm">{message.content || message.text || ""}</div>
              {message.createdAt && (
                <div className="text-xs mt-1 text-right" style={{ opacity: 0.7 }}>
                  {toDate(message.createdAt)}
                </div>
              )}
            </div>
          </div>
        );
      })}
      <div ref={listEndRef} />
    </div>
  );
};
